import { useState } from 'react';
import { Sidebar, SidebarButton, Header, CircularMenuButton, Scrollable, Pagination, useIsReloading, InlineHeader, InlineSection } from '@/components/DashboardComponents'
import { useNavigate, Outlet, useRevalidator } from 'react-router'
import { Button, Textbox, Spacing, RadioGroup, RadioOption, Checkbox } from '@/form/FormComponents'
import { FormProvider, FormDisabled, FormSubmitProvider } from '@/form/FormProvider';
import { Modal, ModalContent, ModalButtons, useErrorModal, useModal, ModalListItem } from '@/components/Modal';
import { useUser, refreshUserdata, sendPasswordResetEmail, sendEmailChangeEmail, changePassword, revokeSession, signOut, deleteAccount } from '@/systems/Authentication';
import { updateProfile, requestToJoinDepartment } from '@/data/ProfileData';
import { Icon } from '@/components/Icon';
import { validateEmail } from '@/shared/utils';
import { GenericEmptyListPlaceholder } from '@/DashboardLayout';
import { useMenu } from '@/components/Menu';
import { publicAction } from '@/data/loaders';
import { SidebarHeaderProvider } from '@/components/DashboardComponents';
import { useBreakpoint } from '@/hooks/useIsMobile';
import { List, ListItem, ItemColumn } from '@/components/List';

export function SettingsLayout() {
  const navigate = useNavigate();
  const { userdata } = useUser();

  return (
    <>
      <div className="dashboard_container" id="settings_layout">
        <header className="dashboard_topbar">
          <div className="topbar_left">
            <CircularMenuButton onClick={() => navigate(-1)} icon="arrow-left"/>
            <div className="topbar_title">Account settings</div>
          </div>
        </header>
        <div className="dashboard_main">
          <SidebarHeaderProvider>
            <Sidebar>
              <SidebarButton icon="account-outline" to="/settings/profile" replace={true} name="Profile"/>
              <SidebarButton icon="lock-outline" to="/settings/security" replace={true} name="Email and security"/>
              {(userdata.role == "educator" || userdata.is_educator || userdata.organizations.length > 0 || userdata.organizations_pending.length > 0) && <SidebarButton icon="domain" to="/settings/organizations" replace={true} name="My departments"/>}
            </Sidebar>
            <Scrollable className="dashboard_content">
              <Outlet/>
            </Scrollable>
          </SidebarHeaderProvider>
        </div>
      </div>
    </>
  )
}

export function Profile({}) {
  const revalidator = useRevalidator();
  const { user, userdata } = useUser();
  const initial_data = {
    name: userdata.name,
    email: user.email,
    role: userdata.role
  }
  const [submitting, setIsSubmitting] = useState(false);
  const is_reloading = useIsReloading();
  const [openErrorModal] = useErrorModal();

  function submitForm({ formatted_data }) {
    setIsSubmitting(true);

    updateProfile({
      name: formatted_data.name,
      ...(formatted_data.role ? {role: formatted_data.role} : {})
    }).then(function(data) {
      setIsSubmitting(false);
      refreshUserdata();
      revalidator.revalidate();
    }).catch(function(error) {
      setIsSubmitting(false);
      openErrorModal();
    })
  }
  return (
    <FormProvider onSubmit={submitForm} initial_data={initial_data} unsaved_modal="settings_profile">
      <Header title="Profile"/>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <div className="dashboard_content_container">
          <Textbox name="name" title="Your name" autofocus={true} required={true}/>
          {userdata.organizations.length == 0 ? (
            <>
              <Spacing/>
              <RadioGroup title="I am a..." name="role" required="Please select your role">
                <RadioOption name="student" text="Learner"/>
                <RadioOption name="educator" text="Educator"/>
              </RadioGroup>
            </>
          ) : null}
        </div>
      </div>
      <Pagination getContainerElement={() => document.querySelector("#settings_layout")}>
        <FormSubmitProvider>
          {({handleSubmit, has_changed}) => {
            return <Button className="primary" text="Save changes" onClick={handleSubmit} disabled={submitting || !has_changed || is_reloading} loading={submitting}/>
          }}
        </FormSubmitProvider>
      </Pagination>
    </FormProvider>
  )
}

export function EmailAndSecurity({}) {
  const { user, userdata } = useUser();
  const initial_data = {
    name: userdata.name,
    email: user.email,
    role: userdata.role
  }
  const provider = user.providerData.length > 1 ? user.providerData.find(p => p.providerId !== "password").providerId : user.providerData[0].providerId;

  const [openErrorModal] = useErrorModal();

  const [openForgotPasswordModal] = useModal(({ closeModal, setLoading }) => {
    const resetPassword = function() {
      setLoading(true);
      sendPasswordResetEmail(user.email).then(function() {
        closeModal();
        openForgotPasswordSuccessModal();
      }).catch(function(error) {
        closeModal();
        console.error(error);
        openErrorModal();
      });
    }

    return (
      <Modal title="Forgot your password?">
        <ModalContent>
          We'll email you a link to choose a new password.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Reset password" className="primary" onClick={resetPassword}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openForgotPasswordSuccessModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Check your email">
        <ModalContent>
          We just emailed you a link to choose a new password.
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openChangeEmailModal] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError, updateData }) {
      if (!validateEmail(formatted_data.email)) {
        focusError({name: "email", error: "Please enter a valid email address"})
        return;
      }

      setLoading(true);
      
      sendEmailChangeEmail(formatted_data.email, formatted_data.password).then(function() {

        closeModal();
        openChangeEmailSuccessModal({new_email: formatted_data.email});

      }).catch(function(error) {
        setLoading(false);

        updateData((draft) => {
          draft.password = "";
        })

        if (error.code == "auth/wrong-password") {
          focusError({name: "password", error: "Incorrect password"});
        } else {
          console.error(error);
          focusError({name: "password", error: "Something went wrong."});
        }
      })
    }

    return (
      <Modal title="Change your email">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="email" title="New email" placeholder="Email" required={true} autoComplete="email" />
                  <Spacing/>
                  <Textbox name="password" title="Password" placeholder="Password" required={true} type="password" autoComplete="password"/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openChangeEmailSuccessModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title="Check your new email">
        <ModalContent>
          <span>Please click the verification link in the email we just sent to <span style={{fontWeight: "500"}}>{context.new_email}</span>. Your account email won't change until you click that link.</span>
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openChangePasswordModal] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError, updateData }) {

      if (formatted_data.new_password !== formatted_data.password_verify) {
        focusError({name: "password_verify", select: true, error: "Passwords don't match"})
        return;
      }

      setLoading(true);
      
      changePassword(formatted_data.password, formatted_data.new_password).then(function() {

        closeModal();
        openChangePasswordSuccessModal();

      }).catch(function(error) {
        setLoading(false);

        updateData((draft) => {
          draft.password = "";
          draft.new_password = "";
          draft.password_verify = "";
        })

        if (error.code == "auth/wrong-password") {
          focusError({name: "password", error: "Incorrect password"});
        } else if (error.code == "auth/weak-password") {
          focusError({name: "new_password", error: "Please choose a stronger password"});
        } else {
          console.error(error);
          focusError({name: "new_password", error: error.message});
        }
      })
    }

    return (
      <Modal title="Change your password">
        <FormProvider onSubmit={submitForm}>
          <form onSubmit={(event) => event.preventDefault()} className="pmf-change-password">
            <FormSubmitProvider>
              {({handleSubmit}) => (
                <>
                  <ModalContent>
                    <Textbox name="password" title="Current password" placeholder="Password" required={true} type="password" autoComplete="current-password" className="pmf-password"/>
                    <Spacing/>
                    <Textbox name="new_password" title="New password" placeholder="Password" required="Please choose a new password" type="password" autoComplete="new-password" className="pmf-new-password"/>
                    <Spacing/>
                    <Textbox name="password_verify" title="Re-type new password" placeholder="Password" required="Please re-type your new password" type="password" autoComplete="new-password" className="pmf-password-verify"/>
                  </ModalContent>
                  <ModalButtons>
                    <Button text="Cancel" className="hollow" onClick={closeModal}/>
                    <Button text="Submit" className="primary" onClick={handleSubmit}/>
                  </ModalButtons>
                </>
              )}
            </FormSubmitProvider>
          </form>
        </FormProvider>
      </Modal>
    )
  })

  const [openChangePasswordSuccessModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Password changed">
        <ModalContent>
          Your password has been changed.
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openRevokeSessionModal] = useModal(({ closeModal, setLoading }) => {
    const confirmRevoke = function() {
      setLoading(true);
      revokeSession().then(function() {
        closeModal();
        signOut(true);
      }).catch(function(error) {
        closeModal();
        console.error(error);
        openErrorModal();
      });
    }

    return (
      <Modal title="Sign out of all devices?">
        <ModalContent>
          You'll be immediately signed out of all devices, including this one.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Sign out of all devices" className="primary" onClick={confirmRevoke}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openDeleteAccountModal] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError, updateData }) {

      if (provider !== "password") {
        if (formatted_data.iunderstand.toLowerCase() !== "i understand") {
          focusError({name: "iunderstand", error: 'Please type "I understand" to confirm'});
          return;
        }
      }

      setLoading(true);
      
      deleteAccount(provider, formatted_data.password).then(function() {

        closeModal();
        signOut(true);

      }).catch(function(error) {
        setLoading(false);

        updateData((draft) => {
          draft.password = "";
        })

        if (error.code == "auth/wrong-password") {
          focusError({name: "password", error: "Incorrect password"});
        } else if (error.code == "auth/requires-recent-login") {
          closeModal();
          openRequiresRecentLoginModal();
        } else {
          console.error(error);
          focusError({name: "password", error: "Something went wrong."});
        }
      })
    }

    return (
      <Modal title="Delete your account?">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  {`You can't undo this.`}
                  <Spacing/>
                  {`If you are a learner, your submissions, feedback, and scoring will not be deleted. Only your instructor can delete that.`}
                  <Spacing/>
                  {`If you are an educator, your scenarios, rubrics, and courses will not automatically be deleted, as they may be shared with others in your organization. If you wish to delete this data, please do so before closing your account, or contact our support team for assistance.`}
                  <Spacing/>
                  {provider == "password" ? (
                    <Textbox name="password" title="Type your password to confirm" placeholder="Password" required={true} type="password" autoComplete="password"/>
                  ) : (
                    <Textbox name="iunderstand" title={`Please type "I understand" to confirm:`} required={'Please type "I understand" to confirm'}/>
                  )}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Delete account" className="destructive" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openRequiresRecentLoginModal] = useModal(({ closeModal, setLoading }) => {
    return (
      <Modal title="Please sign in again, then try again">
        <ModalContent>
          Your account was not deleted. In order to delete your account, you need to have recently signed in. Please sign out, then sign in again, then try to delete your account again. This is for your security.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Sign out" className="primary" onClick={signOut}/>
        </ModalButtons>
      </Modal>
    )
  })

  return (
    <FormProvider initial_data={initial_data} unsaved_modal="settings_email_and_security">
      <Header title="Email and security"/>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <div className="dashboard_content_container">
          {userdata.organization_data ? (
            <div className="callout_hint_box">
              <div>
                <div><Icon name="domain"/></div>
                <div>Your account is managed by {userdata.organization_data.shortname}. Some settings may be limited by your organization.</div>
              </div>
            </div>
          ) : null}
          <InlineHeader title="Email"/>
          <InlineSection>
            <FormDisabled readOnly={true}>
              <Textbox name="email" required={true} placeholder="Email" autoComplete="email" />
            </FormDisabled>
            {provider !== "password" ? <>
              <Spacing/>
              <div className="social_connection_box">
                <div><Icon name={{"google.com": "google", "microsoft.com": "microsoft"}[provider]}/></div>
                <div>Your email is tied to your {{"google.com": "Google", "microsoft.com": "Microsoft"}[provider]} account and can't be changed.</div>
              </div>
            </> : <>
              <Spacing/>
              <Button text="Change email" onClick={openChangeEmailModal}/>
            </>}
          </InlineSection>
          <InlineHeader title="Security"/>
          <InlineSection>
            {provider !== "password" ? <>
              <div className="social_connection_box">
                <div><Icon name={{"google.com": "google", "microsoft.com": "microsoft"}[provider]}/></div>
                <div>{userdata.organization_data ? "Your organization signs in" : "You sign in"} with {{"google.com": "Google", "microsoft.com": "Microsoft"}[provider]}.</div>
              </div>
            </> : <>
              <div className="buttons">
                <Button text="Change password" onClick={openChangePasswordModal}/>
                <Button text="Forgot password" className="hollow" onClick={openForgotPasswordModal}/>
              </div>
            </>}
          </InlineSection>
          <InlineHeader title="Advanced"/>
          <InlineSection>
            <div className="buttons">
              <Button text="Sign out of all devices" onClick={openRevokeSessionModal}/>
              <Button text="Delete account" className="destructive" onClick={openDeleteAccountModal}/>
            </div>
          </InlineSection>
        </div>
      </div>
    </FormProvider>
  )
}

export function useJoinDepartmentModals(fromSignup) {
  const revalidator = useRevalidator();
  const [openErrorModal] = useErrorModal();

  const [openRequestToJoinDepartmentModal] = useModal(({ context, closeModal, setLoading }) => {
    const submitJoinRequest = function() {
      setLoading(true);

      requestToJoinDepartment({
        id: context.department._id
      }).then(function(result) {
        
        closeModal();
        if (result.data.success) {
          if (fromSignup) {
            fromSignup.onSuccess();
          } else {
            openSubmittedModal();
            refreshUserdata();
            revalidator.revalidate();
          }
        } else {
          if (fromSignup) {
            fromSignup.onSuccess();
          } else {
            openAlreadyRequestedModal();
          }
        }

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })
    }

    if (!context.department.join_requests) {
      return (
        <Modal title="Contact the department manager">
          <ModalContent>
            {"This department isn't accepting join requests. Please contact the department manager directly and ask them to add you."}
            {fromSignup ? <>
              <Spacing/>
              {"In the meantime, you can continue with limited access."}
            </> : ""}
          </ModalContent>
          <ModalButtons>
            {fromSignup ? (
              <>
                <Button text="Cancel" className="hollow" onClick={closeModal}/>
                <Button text="Okay, will do" onClick={() => {closeModal(); fromSignup.onSkip();}}/>
              </>
            ) : (
              <Button text="Got it" onClick={closeModal}/>
            )}
          </ModalButtons>
        </Modal>
      )
    }

    return (
      <Modal title={`Request to join ${context.department.name}?`}>
        <ModalContent>
          {"If approved, you will gain educator access and be able to create courses in this department."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Request to join" className="primary" onClick={submitJoinRequest}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openSubmittedModal] = useModal(({ closeModal }) => {
    return (
      <Modal width={330}>
        <ModalContent>
          <div className="assignment_submitted">
            <Icon name="check-circle"/>
            <div>Request sent!</div>
            <div>{`We'll email you once your request is approved or denied.`}</div>
          </div>
        </ModalContent>
        <ModalButtons centered={true}>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });

  const [openAlreadyRequestedModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title="Already requested">
        <ModalContent>
          You've already requested to join this department. You'll be notified once your request is approved.
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  return { openRequestToJoinDepartmentModal, openSubmittedModal, openAlreadyRequestedModal }
}

export function useJoinDepartmentModal({ callback}) {
  const { userdata } = useUser();
  const [parents, setParents] = useState([]);

  const [openJoinDepartmentModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title={parents.length > 0 ? `Departments in ${context.departments.find(d => d._id == parents[parents.length - 1]).name}` : `Join a department at ${userdata.organization_data.shortname}`} onBack={parents.length > (context.initial_parent ? 1 : 0) ? () => {setParents(parents.slice(0, parents.length - 1))} : undefined}>
        <ModalContent>
          {[...context.departments].filter(d => (context.departments.length > 1 ? (parents.length == 0 ? d.parent == context.organization_id : d.parent == parents[parents.length - 1]) : true)).sort((a, b) => a.name.localeCompare(b.name)).map((department) => 
            <ModalListItem key={department._id} icon="domain" text={department.name} onClick={() => {
              if (department.children_type == "organizations") {
                setParents(parents.concat(department._id));
              } else {
                callback({department});
                closeModal();
              }
            }}/>
          )}
          <Spacing/>
          {"Don’t see your department? Contact the Sapient Clinician team to sign up."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, [parents])

  const handleOpenJoinDepartmentModal = function(data) {
    setParents(data.initial_parent ? [data.initial_parent] : []);
    openJoinDepartmentModal(data);
  }

  return [ handleOpenJoinDepartmentModal ];
}

export function useListThenJoinDepartmentModal({ setLoadingJoinDepartment } = {}) {
  const [openErrorModal] = useErrorModal();

  const { openRequestToJoinDepartmentModal } = useJoinDepartmentModals();

  const [openJoinDepartmentModal] = useJoinDepartmentModal({ callback: openRequestToJoinDepartmentModal });

  const handleJoinDepartment = function(args) {
    if (args.setLoadingJoinDepartment) {
      setLoadingJoinDepartment = args.setLoadingJoinDepartment;
    }

    setLoadingJoinDepartment(true);

    publicAction("getMyOrganizationDepartments", {}).then(function(data) {
      setLoadingJoinDepartment(false);
      openJoinDepartmentModal(data.data);
    }).catch(function(error) {
      console.error(error);
      setLoadingJoinDepartment(false);
      openErrorModal();
    })
  }

  return { handleJoinDepartment }
}

export function OrganizationAccess({}) {
  const navigate = useNavigate();
  const { user, userdata } = useUser();

  const [loading_join_department, setLoadingJoinDepartment] = useState(false);

  const { handleJoinDepartment } = useListThenJoinDepartmentModal({ setLoadingJoinDepartment });

  const combined_organizations = [...userdata.organizations_pending.map(a => ({...a, pending: true})).sort((a, b) => {
    const rootNameCompare = a.root_data.name.localeCompare(b.root_data.name);
    return rootNameCompare !== 0 ? rootNameCompare : a.data.name.localeCompare(b.data.name);
  }), ...userdata.organizations.map(a => ({...a, pending: false})).sort((a, b) => {
    const rootNameCompare = a.root_data.name.localeCompare(b.root_data.name);
    return rootNameCompare !== 0 ? rootNameCompare : a.data.name.localeCompare(b.data.name);
  })];

  return <>
    <Header title="My departments">
      {userdata.organization_id ? <Button icon="plus" text="Join a department" className={userdata.organizations.length > 0 ? "" : "primary"} onClick={handleJoinDepartment} loading={loading_join_department}/> : null}
    </Header>
    <div className="dashboard_content_inner nearest_scrollable_container">
      <div className="dashboard_content_container">
        <div className="callout_hint_box">
          <div>
            <div><Icon name="domain"/></div>
            <div>{userdata.organizations.length > 0 ? `Your account has educator access through the departments listed below. You can create courses in these departments.` : `Your account is limited without educator access. You must request to join, be invited to join, or sign up your ${userdata.organization_id ? "department" : "organization"} to get educator access.`}</div>
          </div>
        </div>
        {combined_organizations.length > 0 ? (
          <List columns={[
            { name: "Department" },
            { name: "Organization", width: 250, hide_order: 1 },
            { name: "Role", width: 110, hide_order: 2 },
          ]} menu={true}>
            {combined_organizations.map(function(organization) {
              return <OrganizationItem key={organization.data._id} organization={organization}/>
            })}
          </List>
        ) : <GenericEmptyListPlaceholder text="You aren't in any departments, so you don't have educator access." icon="domain-off"/>}
      </div>
    </div>
  </>
}

function OrganizationItem({organization}) {
  const revalidator = useRevalidator();
  const { userdata, uid } = useUser();

  const [openErrorModal] = useErrorModal();

  const [openRemoveYourselfModal] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {
      setLoading(true);

      publicAction("removeOrganizationMember", {
        organization_id: organization.data._id,
        id: uid,
        remove_from_courses: !organization.pending && formatted_data.remove_from_courses
      }).then(function() {
        closeModal();
        refreshUserdata();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })

    }

    return (
      <Modal title={organization.pending ? `Cancel request to join ${organization.data.name}?` : `Leave ${organization.data.name}?`}>
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  {organization.pending ? "You can always request to join again later." : <>
                    <Checkbox name="remove_from_courses" text="Also leave any courses I teach in this department" default_value={false}/>
                  </>}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text={organization.pending ? "Cancel request" : "Leave department"} className={!organization.pending ? "destructive" : "primary"} onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const menu = useMenu({
    menuItems: [
      {
        label: organization.pending ? 'Cancel request' : 'Leave',
        icon: 'close',
        onClick: openRemoveYourselfModal
      }
    ]
  });

  return (
    <ListItem icon="domain" menu={menu}>
      <ItemColumn>{organization.data.name}</ItemColumn>
      <ItemColumn><span style={organization.root_data._id != userdata.organization_id ? {color: "var(--yellowtext)"} : undefined}>{organization.root_data.name}</span></ItemColumn>
      <ItemColumn>{organization.pending ? <i>{"Requested..."}</i> : (organization.roles.includes("admin") ? "Manager" : "Educator")}</ItemColumn>
    </ListItem>
  )
}